import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CreditCard, Banknote, ChevronLeft, Loader2, Share2, Video, Star, Heart, ShieldCheck, Mail, Calendar, MapPin, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePaystackPayment } from 'paystack-react';
import '@/App.css';
import success_1 from '@/assets/success_1.jpeg';
import Navigation from '@/components/nav';
import FooterSection from '@/components/footer';

const API_BASE = import.meta.env.VITE_API_BASE || "https://qwaqwatalentshow.co.za";

interface Participant {
    id: number;
    event_id: number;
    stage_name: string;
    talent: string;
    competition?: string | null;
    image?: string | null;
    description?: string | null;
    video_url?: string | null;
    age?: number | null;
    location?: string | null;
    votes: number;
}

interface EventDetails {
    venue_details: string | null;
    price_per_vote: number;
}

interface EventData {
    id: number;
    title: string;
    start_date: string;
    start_time: string;
    category: string;
    details: EventDetails;
    votes_enabled: number;
}

function ParticipantDetail() {
    const { eventID, participantID } = useParams<{ eventID: string; participantID: string }>();
    const navigate = useNavigate();
    
    const [participant, setParticipant] = useState<Participant | null>(null);
    const [event, setEvent] = useState<EventData | null>(null);
    const [loading, setLoading] = useState(true);
    
    const [numberOfVotes, setNumberOfVotes] = useState(1);
    const [paymentMethod, setPaymentMethod] = useState<'online' | 'offline'>('online');
    const [email, setEmail] = useState('');

    const price = event?.details?.price_per_vote || 0;
    const totalPrice = price * numberOfVotes;

    const config = {
        reference: (new Date()).getTime().toString(),
        email: email || "user@example.com",
        amount: totalPrice * 100,
        publicKey: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || 'pk_test_xxx',
    };

    const paystackInitialize = usePaystackPayment(config);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const eventRes = await fetch(`${API_BASE}/api/v1/event?id=${eventID}`);
                const eventData = await eventRes.json();
                
                const participantsRes = await fetch(`${API_BASE}/api/v1/events/${eventID}/participants`);
                const participantsData = await participantsRes.json();
                
                if (eventRes.ok && participantsRes.ok) {
                    setEvent(eventData.event);
                    const participantsArray = Array.isArray(participantsData.participants) ? participantsData.participants : [];
                    const found = participantsArray.find((p: any) => p.id === parseInt(participantID || '0'));
                    if (found) {
                        setParticipant({
                            ...found,
                            competition: found.talent || 'Performance',
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (eventID && participantID) fetchData();
    }, [eventID, participantID]);

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-QWA2_TALENT_SHOW-gold" />
            </div>
        );
    }

    if (!participant || !event) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
                <h1 className="text-2xl font-black mb-4">Participant Not Found</h1>
                <Button onClick={() => navigate(-1)}>Go Back</Button>
            </div>
        );
    }

    const onSuccess = (reference: any) => {
        console.log(reference);
        alert(`✓ Support submitted!\nThank you for supporting ${participant.stage_name}`);
        navigate(`/competitions/${eventID}`);
    };

    const onClosePaystack = () => console.log('Payment closed');

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: `Vote for ${participant.stage_name}`,
                text: `Check out ${participant.stage_name} on QwaQwa Talent Show and cast your vote!`,
                url: window.location.href,
            });
        } else {
            alert("Copy this link to share: " + window.location.href);
        }
    };

    const increaseVotes = () => setNumberOfVotes(prev => prev + 5);
    const decreaseVotes = () => setNumberOfVotes(prev => (prev > 1 ? prev - 1 : 1));

    const renderVideo = () => {
        if (!participant.video_url) return null;
        const isYoutube = participant.video_url.includes('youtube.com') || participant.video_url.includes('youtu.be');
        
        if (isYoutube) {
            const videoId = participant.video_url.split('v=')[1] || participant.video_url.split('/').pop();
            return <iframe className="w-full aspect-video rounded-3xl shadow-xl" src={`https://www.youtube.com/embed/${videoId}`} title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />;
        }
        return (
            <div className="rounded-[32px] overflow-hidden shadow-2xl border-4 border-white/10">
                <video controls className="w-full h-full object-cover">
                    <source src={participant.video_url} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#fafafa]">
            <Navigation />
            <main className="pb-20 relative overflow-hidden">
                {/* Background Decoration */}
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-QWA2_TALENT_SHOW-red/5 blur-[120px] rounded-full translate-y-1/2 -translate-x-1/2" />

                {/* Hero Banner Section */}
<section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center bg-black overflow-hidden mb-16 pt-[120px]">
    <div className="absolute inset-0">
        <img
            src={participant.image ? `${API_BASE}${participant.image}` : success_1}
            alt={participant.stage_name}
            className="w-full h-full object-cover blur-[2px] opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#fafafa] via-transparent to-transparent" />
    </div>

    <div className="relative z-10 max-w-6xl mx-auto px-4 w-full">
        <button
            onClick={() => navigate(-1)}
            className="group flex items-center gap-2 text-white/70 hover:text-white transition-all mb-8 md:mb-12 font-bold text-xs uppercase tracking-[0.2em]"
        >
            <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            Back to Competition
        </button>

        <div className="flex flex-col md:flex-row items-center md:items-end gap-6 md:gap-8 mb-12">
            <div className="relative shrink-0">
                <div className="absolute -inset-4 bg-gradient-to-tr from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold rounded-[40px] blur-2xl opacity-30 animate-pulse" />

                <img
                    src={participant.image ? `${API_BASE}${participant.image}` : success_1}
                    alt={participant.stage_name}
                    className="w-40 h-40 sm:w-48 sm:h-48 md:w-64 md:h-64 rounded-[40px] object-cover border-4 border-white/20 shadow-2xl relative z-10"
                />

                <div className="absolute -bottom-2 -right-2 w-14 h-14 md:w-16 md:h-16 bg-white rounded-2xl flex items-center justify-center shadow-2xl z-20">
                    <Star className="w-7 h-7 md:w-8 md:h-8 text-QWA2_TALENT_SHOW-gold fill-QWA2_TALENT_SHOW-gold" />
                </div>
            </div>

            <div className="flex-1 space-y-4 pb-4 text-center md:text-left">
                <div className="flex flex-wrap justify-center md:justify-start gap-3">
                    <span className="px-4 py-1.5 rounded-full bg-QWA2_TALENT_SHOW-gold text-black text-[10px] font-black uppercase tracking-widest">
                        {event.title}
                    </span>
                </div>

                <h1 className="text-4xl sm:text-5xl md:text-8xl font-black text-white leading-[0.9] tracking-tighter drop-shadow-2xl break-words">
                    {participant.stage_name}
                </h1>

                <p className="text-lg sm:text-xl md:text-2xl text-white/60 font-medium tracking-wide uppercase [text-shadow:0_4px_12px_rgba(0,0,0,1)]">
    {participant.talent}
</p>
            </div>
        </div>
    </div>
</section>

                <div className="max-w-6xl mx-auto px-4 relative z-10">
                    {/* Content Grid */}
                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Left Column: Content */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Video Section */}
                            {participant.video_url && (
                                <section className="space-y-4">
                                    <div className="flex items-center gap-3 text-QWA2_TALENT_SHOW-black font-black uppercase tracking-[3px] text-sm">
                                        <Video className="w-5 h-5 text-QWA2_TALENT_SHOW-red" />
                                        Performance Showcase
                                    </div>
                                    {renderVideo()}
                                </section>
                            )}

                            {/* About Section */}
                            <section className="bg-white rounded-[32px] p-8 md:p-10 border border-gray-100 shadow-sm">
                                <h3 className="text-2xl font-black text-QWA2_TALENT_SHOW-black mb-6">About the Artist</h3>
                                <p className="text-gray-600 leading-relaxed text-lg whitespace-pre-line">
                                    {participant.description || "No description available for this participant."}
                                </p>
                                
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-10 pt-8 border-t border-gray-50">
                                    {participant.location && (
                                        <div>
                                            <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1">Location</p>
                                            <p className="font-bold text-QWA2_TALENT_SHOW-black">{participant.location}</p>
                                        </div>
                                    )}
                                    {participant.age && (
                                        <div>
                                            <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1">Age</p>
                                            <p className="font-bold text-QWA2_TALENT_SHOW-black">{participant.age} Years</p>
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1">Status</p>
                                        <p className="font-bold text-green-500 flex items-center gap-1.5">
                                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                            Active
                                        </p>
                                    </div>
                                </div>
                            </section>

                            {/* Event Information Section */}
                            <section className="bg-white rounded-[32px] p-8 md:p-10 border border-gray-100 shadow-sm">
                                <h3 className="text-2xl font-black text-QWA2_TALENT_SHOW-black mb-6">Event Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-2xl bg-QWA2_TALENT_SHOW-gold/10 flex items-center justify-center shrink-0">
                                            <Calendar className="w-6 h-6 text-QWA2_TALENT_SHOW-gold" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1">Date & Time</p>
                                            <p className="font-bold text-QWA2_TALENT_SHOW-black">
                                                {new Date(event.start_date).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                            </p>
                                            <p className="text-sm text-gray-500">{event.start_time?.substring(0, 5)}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-2xl bg-QWA2_TALENT_SHOW-red/10 flex items-center justify-center shrink-0">
                                            <MapPin className="w-6 h-6 text-QWA2_TALENT_SHOW-red" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1">Venue</p>
                                            <p className="font-bold text-QWA2_TALENT_SHOW-black leading-tight">
                                                {event.details?.venue_details || 'Venue to be announced'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center shrink-0">
                                            <Tag className="w-6 h-6 text-blue-500" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1">Category</p>
                                            <p className="font-bold text-QWA2_TALENT_SHOW-black">{event.category || 'General'}</p>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>

                        {/* Right Column: Voting & Payment */}
                        <div className="space-y-8">
                            {event.votes_enabled === 1 ? (
                                <div className="sticky top-24 space-y-8">
                                    {/* Voting Card */}
                                    <section className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl overflow-hidden relative group">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-QWA2_TALENT_SHOW-gold/5 blur-2xl rounded-full" />
                                        
                                        <h3 className="text-xl font-black text-QWA2_TALENT_SHOW-black mb-6">Support Contestant</h3>
                                        
                                        <div className="space-y-6">
                                            <div className="flex items-center gap-4">
                                                <button 
                                                    onClick={decreaseVotes} 
                                                    className="w-16 h-16 rounded-2xl bg-gray-50 hover:bg-gray-100 text-QWA2_TALENT_SHOW-black text-2xl font-bold transition flex items-center justify-center border border-gray-100"
                                                >
                                                    −
                                                </button>
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="number"
                                                        value={numberOfVotes}
                                                        onChange={(e) => setNumberOfVotes(parseInt(e.target.value) || 1)}
                                                        className="w-full h-16 text-center text-3xl font-black bg-gray-50 border-2 border-transparent rounded-2xl focus:border-QWA2_TALENT_SHOW-gold transition-all outline-none text-QWA2_TALENT_SHOW-black"
                                                    />
                                                    <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[8px] uppercase font-black text-gray-400 tracking-widest">Votes</span>
                                                </div>
                                                <button 
                                                    onClick={increaseVotes} 
                                                    className="w-16 h-16 rounded-2xl bg-QWA2_TALENT_SHOW-gold/10 hover:bg-QWA2_TALENT_SHOW-gold/20 text-QWA2_TALENT_SHOW-gold text-2xl font-bold transition flex items-center justify-center border border-QWA2_TALENT_SHOW-gold/10"
                                                >
                                                    +
                                                </button>
                                            </div>

                                            <div className="bg-[#111111] rounded-[24px] p-6 text-white relative overflow-hidden">
                                                <div className="absolute top-0 right-0 w-24 h-24 bg-QWA2_TALENT_SHOW-red/20 blur-2xl rounded-full" />
                                                <div className="relative z-10 flex justify-between items-end">
                                                    <div>
                                                        <p className="text-[10px] uppercase font-black tracking-widest text-white/40 mb-1">Total Amount</p>
                                                        <p className="text-4xl font-black tracking-tighter">R{totalPrice.toLocaleString()}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-[10px] font-bold text-white/30">R{price} per vote</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </section>

                                    {/* Payment Card */}
                                    <div className="space-y-8">
                                        <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                                            <h3 className="text-xl font-black text-QWA2_TALENT_SHOW-black mb-6 flex items-center gap-2">
                                                <ShieldCheck className="w-5 h-5 text-QWA2_TALENT_SHOW-gold" /> 
                                                Checkout Securely
                                            </h3>
                                            
                                            <div className="space-y-3 mb-8">
                                                <label className={`group flex items-center gap-4 p-4 border-2 rounded-[20px] cursor-pointer transition-all ${paymentMethod === 'online' ? 'border-QWA2_TALENT_SHOW-gold bg-QWA2_TALENT_SHOW-gold/5' : 'border-gray-50 hover:border-gray-200'}`}>
                                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${paymentMethod === 'online' ? 'border-QWA2_TALENT_SHOW-gold' : 'border-gray-300'}`}>
                                                        {paymentMethod === 'online' && <div className="w-3 h-3 bg-QWA2_TALENT_SHOW-gold rounded-full" />}
                                                    </div>
                                                    <CreditCard className={`w-5 h-5 ${paymentMethod === 'online' ? 'text-QWA2_TALENT_SHOW-gold' : 'text-gray-400'}`} />
                                                    <div className="flex-1">
                                                        <p className="font-black text-sm text-QWA2_TALENT_SHOW-black">Instant Online</p>
                                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Secure via Paystack</p>
                                                    </div>
                                                    <input type="radio" checked={paymentMethod === 'online'} onChange={() => setPaymentMethod('online')} className="hidden" />
                                                </label>

                                                <label className={`group flex items-center gap-4 p-4 border-2 rounded-[20px] cursor-pointer transition-all ${paymentMethod === 'offline' ? 'border-QWA2_TALENT_SHOW-gold bg-QWA2_TALENT_SHOW-gold/5' : 'border-gray-50 hover:border-gray-200'}`}>
                                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${paymentMethod === 'offline' ? 'border-QWA2_TALENT_SHOW-gold' : 'border-gray-300'}`}>
                                                        {paymentMethod === 'offline' && <div className="w-3 h-3 bg-QWA2_TALENT_SHOW-gold rounded-full" />}
                                                    </div>
                                                    <Banknote className={`w-5 h-5 ${paymentMethod === 'offline' ? 'text-QWA2_TALENT_SHOW-gold' : 'text-gray-400'}`} />
                                                    <div className="flex-1">
                                                        <p className="font-black text-sm text-QWA2_TALENT_SHOW-black">Manual Transfer</p>
                                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Verification needed</p>
                                                    </div>
                                                    <input type="radio" checked={paymentMethod === 'offline'} onChange={() => setPaymentMethod('offline')} className="hidden" />
                                                </label>
                                            </div>

                                            {paymentMethod === 'online' ? (
                                                <div className="space-y-4">
                                                    <div className="relative">
                                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                        <Input 
                                                            type="email" 
                                                            placeholder="Email for receipt" 
                                                            value={email} 
                                                            onChange={(e) => setEmail(e.target.value)} 
                                                            className="h-14 pl-11 rounded-2xl border-gray-100 bg-gray-50 focus:bg-white text-QWA2_TALENT_SHOW-black font-semibold"
                                                        />
                                                    </div>
                                                    <Button 
                                                        onClick={() => paystackInitialize(onSuccess, onClosePaystack)}
                                                        disabled={!email}
                                                        className="w-full h-16 bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold text-white font-black uppercase tracking-[2px] rounded-2xl shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                                                    >
                                                        Complete Payment
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="bg-gray-50 rounded-[24px] p-6 border border-dashed border-gray-200 space-y-4">
                                                    <h4 className="font-black text-sm uppercase tracking-widest text-gray-400">Banking Info</h4>
                                                    <div className="space-y-3 text-sm">
                                                        <div className="flex justify-between items-center"><span className="text-gray-500">Bank</span><span className="font-bold text-QWA2_TALENT_SHOW-black">Capitec Bank</span></div>
                                                        <div className="flex justify-between items-center"><span className="text-gray-500">Account</span><span className="font-bold text-QWA2_TALENT_SHOW-black">2528410898</span></div>
                                                        <div className="flex flex-col gap-1 pt-2">
                                                            <span className="text-[10px] font-black uppercase text-QWA2_TALENT_SHOW-red tracking-widest">Reference (Required)</span>
                                                            <div className="bg-white p-3 rounded-xl border border-gray-100 font-black text-center text-QWA2_TALENT_SHOW-black">
                                                                Q2TSE{eventID}P{participantID}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm text-center space-y-4">
                                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto text-gray-400">
                                        {/* <Info className="w-8 h-8" /> */}
                                    </div>
                                    <h3 className="text-xl font-black text-QWA2_TALENT_SHOW-black">Voting Closed</h3>
                                    <p className="text-gray-500 text-sm">Voting is currently disabled for this competition. Stay tuned for the results!</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <FooterSection />
        </div>
    );
}

export default ParticipantDetail;